import React, { Dispatch, ReactNode, SetStateAction, useState, useEffect } from 'react'
import { useExpandedComponentControl, useShippingOption } from '../../state'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ShippingOptionErrorType, ExpandableComponent } from '../../model'
import { useIsTheme, Theme } from '@afterpaytouch/core'

export interface ShippingOptionContextProps {
  isExpanded: boolean
  handleExpandedStateChange: () => void
  isCollapse: boolean
  setIsCollapse: Dispatch<SetStateAction<boolean>>
  showWarningMessage: boolean
  setShowWarningMessage: Dispatch<SetStateAction<boolean>>
  setIsExpanded: Dispatch<SetStateAction<boolean>>
}

const ShippingOptionContextDefaultValues = {
  isExpanded: false,
  handleExpandedStateChange: () => {},
  isCollapse: false,
  setIsCollapse: (value: boolean) => {},
  showWarningMessage: false,
  setShowWarningMessage: () => {},
  setIsExpanded: (value: boolean) => {},
}

export const ShippingOptionContext = React.createContext<ShippingOptionContextProps>(ShippingOptionContextDefaultValues)

export const ShippingOptionProvider = ({ children }): ReactNode => {
  const { selectedShippingOption, error } = useShippingOption()
  const isShippingOptionUnableToUpdate = error === ShippingOptionErrorType.ShippingOptionUnableToUpdate
  const isCashTheme = useIsTheme(Theme.Cash)
  const hasSelectedShippingOption = valueIsNotNullOrUndefined(selectedShippingOption)
  const { toggleExpandedState, setExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.SHIPPING_OPTION)

  const isExpanded = expandableComponentState === 'Expanded'
  const setIsExpanded = (value: boolean): void => {
    setExpandedState(value ? 'Expanded' : 'Collapsed')
  }

  const [isCollapse, setIsCollapse] = useState(hasSelectedShippingOption)
  const [showWarningMessage, setShowWarningMessage] = useState(false)

  const handleExpandedStateChange = (): void => {
    toggleExpandedState()
  }

  useEffect(() => {
    if (isShippingOptionUnableToUpdate && isCashTheme) {
      setIsCollapse(false)
    }
  }, [isShippingOptionUnableToUpdate, isCashTheme])

  return (
    <ShippingOptionContext.Provider
      value={{
        isExpanded,
        handleExpandedStateChange,
        isCollapse,
        setIsCollapse,
        showWarningMessage,
        setShowWarningMessage,
        setIsExpanded,
      }}
    >
      {children}
    </ShippingOptionContext.Provider>
  )
}
